import type { ExpandedState, SortingState, Updater } from "@tanstack/react-table"
import type { RefObject } from "react"
import { useShallow } from "zustand/react/shallow"

import type { AdjustmentHistorySearchParams } from "../schemas"
import { useAdjustmentHistorySearchStore } from "../stores/useAdjustmentHistorySearchStore"

import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"

interface UseAdjustmentHistoryListReturn {
  adjustmentHistoryItems: AdjustmentHistoryItem[]
  isFetching: boolean
  error: Error | undefined
  sorting: SortingState
  expanded: ExpandedState
  searchParams: AdjustmentHistorySearchParams
  tableContainerRef: RefObject<HTMLDivElement>
  refetch: () => void
  handleSortingChange: (updater: Updater<SortingState>) => void
  handleExpandedChange: (updater: Updater<ExpandedState>) => void
  handleSearchChange: (query: string) => void
  handleFilterChange: <K extends keyof AdjustmentHistorySearchParams>(
    key: K,
    value: AdjustmentHistorySearchParams[K]
  ) => void
  handleFilterRemove: (key: keyof AdjustmentHistorySearchParams, value?: string | number) => void
  handleFilterClear: () => void
  handleScroll: () => void
}

const ROUTE = "/app/inventory/_layout/adjustment-history"

const FILTER_KEYS: (keyof AdjustmentHistorySearchParams)[] = [
  "adjustmentTypes",
  "adjustmentReasons",
  "statuses",
  "warehouseAreaIds",
  "adjustedByIds",
  "dateFrom",
  "dateTo",
]

export const useAdjustmentHistoryList = (): UseAdjustmentHistoryListReturn => {
  const store = useAdjustmentHistorySearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )

  const {
    data: adjustmentHistoryItems,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    expanded,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleExpandedChange,
  } = useListPageBase<AdjustmentHistoryItem, AdjustmentHistorySearchParams>({
    route: ROUTE,
    store,
    filterKeys: FILTER_KEYS,
  })

  return {
    adjustmentHistoryItems,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    expanded,
    refetch,
    handleScroll,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleExpandedChange,
  }
}
